import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  addDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import MemoryRetentionIndicator from '../components/MemoryRetentionIndicator';
import { useTranslation } from 'react-i18next';
import { 
  TrashIcon,
  ArrowUpTrayIcon,
  ListBulletIcon,
  Squares2X2Icon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';

export default function UserHistory() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // Default to grid, will be updated in useEffect
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingContentId, setEditingContentId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editWritingStyle, setEditWritingStyle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [editOriginalUrl, setEditOriginalUrl] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Function to calculate opacity based on Ebbinghaus Forgetting Curve
  const calculateOpacity = (lastAccessedDate) => {
    if (!lastAccessedDate) return 1; // If no last accessed date, show full opacity
    
    const now = new Date();
    const lastAccessed = new Date(lastAccessedDate);
    const diffInMinutes = Math.floor((now - lastAccessed) / (1000 * 60));
    
    // Based on Ebbinghaus Forgetting Curve using exponential decay
    // R = e^(-t/S) where:
    // R is retrievability (measure of how easy it is to retrieve information)
    // t is time in minutes
    // S is stability of memory (we'll use different values for different time periods)
    
    // Constants based on Ebbinghaus' findings
    // After 20 minutes: 58% retention
    // After 1 hour: 44% retention
    // After 1 day: 34% retention
    // After 1 week: 21% retention
    
    // Calculate retention using exponential decay formula
    let retention;
    
    if (diffInMinutes < 20) {
      // For first 20 minutes, use stability factor to reach 58% at 20 minutes
      const stabilityFactor = -20 / Math.log(0.58);
      retention = Math.exp(-diffInMinutes / stabilityFactor);
    } else if (diffInMinutes < 60) {
      // For 20 minutes to 1 hour, use stability factor to reach 44% at 60 minutes
      const stabilityFactor = -60 / Math.log(0.44);
      retention = Math.exp(-diffInMinutes / stabilityFactor);
    } else if (diffInMinutes < 1440) { // 24 hours = 1440 minutes
      // For 1 hour to 1 day, use stability factor to reach 34% at 1 day
      const stabilityFactor = -1440 / Math.log(0.34);
      retention = Math.exp(-diffInMinutes / stabilityFactor);
    } else if (diffInMinutes < 10080) { // 7 days = 10080 minutes
      // For 1 day to 1 week, use stability factor to reach 21% at 1 week
      const stabilityFactor = -10080 / Math.log(0.21);
      retention = Math.exp(-diffInMinutes / stabilityFactor);
    } else {
      // More than 1 week - minimum opacity of 21%
      retention = 0.21;
    }
    
    // Ensure minimum opacity is 0.21 (21%)
    return Math.max(retention, 0.21);
  };

  // Function to update last accessed date
  const updateLastAccessed = async (contentId) => {
    try {
      const now = new Date();
      const timestamp = now.toISOString();
      
      // Get the current document to update the access history array
      const contentRef = doc(db, 'contents', contentId);
      const contentSnap = await getDoc(contentRef);
      
      let accessHistory = [];
      if (contentSnap.exists() && contentSnap.data().accessHistory) {
        accessHistory = contentSnap.data().accessHistory;
      }
      
      // Add new access entry to the array
      accessHistory.push({
        timestamp: timestamp,
        type: 'view'
      });
      
      // Limit array size to prevent it from growing too large
      if (accessHistory.length > 100) {
        accessHistory = accessHistory.slice(-100);
      }
      
      // Update the document
      const updateData = {
        lastAccessed: timestamp,
        accessHistory: accessHistory
      };
      
      await updateDoc(contentRef, updateData);
      
      // Update local state
      setContents(prevContents =>
        prevContents.map(c =>
          c.id === contentId ? {
            ...c,
            lastAccessed: timestamp
          } : c
        )
      );
      
      console.log(`Updated last accessed time for content ${contentId}`);
    } catch (error) {
      console.error('Error updating last accessed date:', error);
    }
  };

  useEffect(() => {
    async function fetchUserContents() {
      if (!currentUser) return;
      try {
        const q = query(
          collection(db, 'contents'),
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        console.log(
          'Query results:',
          querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
        const userContents = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setContents(userContents);
      } catch (error) {
        console.error('Error fetching user contents:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchUserContents();
  }, [currentUser]);

  const handleVisibilityToggle = async (contentId, currentVisibility) => {
    try {
      await updateDoc(doc(db, 'contents', contentId), {
        isPublic: !currentVisibility
      });
      setContents((prevContents) =>
        prevContents.map((content) =>
          content.id === contentId
            ? { ...content, isPublic: !currentVisibility }
            : content
        )
      );
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm(t('ui.confirmDelete'))) return;
    try {
      await deleteDoc(doc(db, 'contents', contentId));
      setContents(contents.filter((content) => content.id !== contentId));
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handleUpdateTitle = async (contentId, newTitle) => {
    if (!newTitle.trim()) return;
    try {
      await updateDoc(doc(db, 'contents', contentId), { title: newTitle });
      setContents((prevContents) =>
        prevContents.map((content) =>
          content.id === contentId ? { ...content, title: newTitle } : content
        )
      );
      setEditingId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Error updating title:', error);
    }
  };

  const handleImportMarkdown = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const title = file.name.replace('.md', '');
      const uniqueId = `md-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Split markdown into main sections based on "##"
      const mainSections = text
        .split(/(?=^## )/m)
        .filter((section) => section.trim())
        .map((section) => {
          const lines = section.split('\n');
          const sectionTitle = lines[0].replace(/^## /, '').trim();
          const sectionContent = lines.slice(1).join('\n').trim();
          return {
            title: sectionTitle,
            content: sectionContent
          };
        });

      console.log('Processed sections:', mainSections);

      // Trigger GitHub Action for markdown processing
      try {
        await fetch('https://api.github.com/repos/owner/content2learn-dev/dispatches', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
            Accept: 'application/vnd.github.v3+json'
          },
          body: JSON.stringify({
            event_type: 'process-markdown',
            client_payload: {
              content: text,
              id: uniqueId
            }
          })
        });
      } catch (actionError) {
        console.error('Error triggering GitHub Action:', actionError);
      }

      // Use different keys for the original markdown and the parsed structure
      const newContent = {
        title,
        rawContent: text, // Original markdown
        parsedContent: mainSections, // Processed structure
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
        writingStyle: 'Custom Import',
        isPublic: false,
        markdownId: uniqueId
      };

      const docRef = await addDoc(collection(db, 'contents'), newContent);
      setContents((prevContents) => [
        { id: docRef.id, ...newContent },
        ...prevContents
      ]);
    } catch (error) {
      console.error('Error importing markdown:', error);
      alert(t('ui.importError'));
    }
  };

  const handleUpdateContent = async () => {
    // Prevent empty titles
    if (!editTitle.trim()) {
      alert(t('ui.emptyTitle'));
      return;
    }
    
    try {
      await updateDoc(doc(db, 'contents', editingContentId), {
        title: editTitle.trim(),
        writingStyle: editWritingStyle,
        summary: editSummary,
        originalUrl: editOriginalUrl,
        updatedAt: new Date()
      });

      // Update local state
      setContents(prevContents =>
        prevContents.map(c =>
          c.id === editingContentId ? {
            ...c,
            title: editTitle.trim(),
            writingStyle: editWritingStyle,
            summary: editSummary,
            originalUrl: editOriginalUrl
          } : c
        )
      );

      // Close modal
      setIsEditModalOpen(false);
      // Show success message
      alert(t('ui.updateSuccess'));
    } catch (error) {
      console.error('Error updating content:', error);
      alert(t('ui.updateError') + error.message);
    }
  };

  const filteredContents = contents.filter((content) => {
    const matchesSearch =
      content.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.writingStyle?.toLowerCase().includes(searchTerm.toLowerCase());
    return showStarredOnly ? matchesSearch && content.isStarred : matchesSearch;
  });

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isEditModalOpen) {
        setIsEditModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isEditModalOpen]);

  useEffect(() => {
    // Add viewport meta tag for better mobile responsiveness if not already present
    if (!document.querySelector('meta[name="viewport"]')) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
    
    // Add CSS to prevent iOS zoom on focus for input fields
    const style = document.createElement('style');
    style.innerHTML = `
      @media screen and (max-width: 768px) {
        input, select, textarea {
          font-size: 16px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      // Clean up when component unmounts
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // Initialize viewMode and check if mobile on component mount
  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Set viewMode based on mobile status and localStorage
      const storedViewMode = localStorage.getItem('viewMode');
      if (mobile) {
        setViewMode('grid');
      } else if (storedViewMode) {
        setViewMode(storedViewMode);
      } else {
        setViewMode('list'); // Default for desktop
      }
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Handle view mode toggle
  const handleViewModeToggle = () => {
    if (!isMobile) {
      const newMode = viewMode === 'grid' ? 'list' : 'grid';
      setViewMode(newMode);
      localStorage.setItem('viewMode', newMode);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 sm:mb-8 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold">{t('ui.yourContent')}</h1>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder={t('ui.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            <div className="flex gap-2 justify-between sm:justify-start">
              <button
                onClick={handleViewModeToggle}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isMobile ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label={viewMode === 'grid' ? t('ui.listView') : t('ui.gridView')}
                disabled={isMobile}
              >
                {viewMode === 'grid' ? (
                  <>
                    <ListBulletIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">{t('ui.listView')}</span>
                  </>
                ) : (
                  <>
                    <Squares2X2Icon className="w-5 h-5" />
                    <span className="hidden sm:inline">{t('ui.gridView')}</span>
                  </>
                )}
              </button>
              <button
                className={`flex-1 sm:flex-none px-3 py-2 rounded-lg flex items-center justify-center gap-1 ${
                  showStarredOnly
                    ? 'bg-yellow-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
                onClick={() => setShowStarredOnly(!showStarredOnly)}
                aria-label={showStarredOnly ? t('ui.showAll') : t('ui.showStarred')}
              >
                <StarIcon
                  className={`w-5 h-5 ${
                    showStarredOnly ? 'fill-current' : ''
                  }`}
                />
                <span className="hidden sm:inline">{showStarredOnly ? t('ui.showAll') : t('ui.showStarred')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Section */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-xl text-gray-500 dark:text-gray-400">{t('ui.loading')}</span>
          </div>
        ) : filteredContents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-xl">{t('ui.noContent')}</p>
          </div>
        ) : (
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'
                : 'space-y-4'
            }
          >
            {filteredContents.map((content) => (
              <div
                key={content.id}
                className={`bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all ${
                  viewMode === 'grid'
                    ? 'p-4 sm:p-6'
                    : 'p-3 sm:p-4'
                } relative ${viewMode === 'list' ? 'flex flex-col sm:flex-row items-start sm:items-center gap-4' : ''} cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700`}
                onClick={(e) => {
                  if (
                    e.target.closest('.control-button') ||
                    e.target.closest('.edit-button') ||
                    e.target.closest('.delete-button') ||
                    e.target.closest('.star-button') ||
                    e.target.closest('.visibility-button') ||
                    e.target.closest('.title-text') ||
                    e.target.closest('.info-icon')
                  ) {
                    return;
                  }
                  
                  // Update last accessed date
                  updateLastAccessed(content.id);
                  
                  if (content.externalUrl) {
                    setContents((prevContents) =>
                      prevContents.map((c) =>
                        c.id === content.id ? { ...c, isExpanded: true } : c
                      )
                    );
                  } else {
                    window.location.href = `/viewer/${content.id}`;
                  }
                }}
              >
                {/* Memory Retention Progress Bar - Moved to bottom with thinner height and subtle colors */}
                {content.lastAccessed && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-300 dark:bg-gray-700 rounded-b-lg overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-600/60 to-green-500/60" 
                      style={{ 
                        width: `${Math.round(calculateOpacity(content.lastAccessed) * 100)}%`,
                        transition: 'width 0.5s ease-in-out'
                      }}
                    ></div>
                  </div>
                )}

                {/* Visible Mastery Level Indicator for List View - More subtle */}
                {content.lastAccessed && viewMode === 'list' && (
                  <div className="absolute bottom-1 right-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span className="text-green-600/80 dark:text-green-500/80">{t('ui.memoryRetention', { percentage: Math.round(calculateOpacity(content.lastAccessed) * 100) })}</span>
                  </div>
                )}

                {/* Visible Mastery Level Indicator for Grid View - More subtle */}
                {content.lastAccessed && viewMode === 'grid' && (
                  <div className="absolute bottom-1 right-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span className="text-green-600/80 dark:text-green-500/80">{t('ui.memoryRetention', { percentage: Math.round(calculateOpacity(content.lastAccessed) * 100) })}</span>
                  </div>
                )}
                {/* Memory Retention Indicator */}
                <MemoryRetentionIndicator 
                  lastAccessed={content.lastAccessed} 
                  calculateOpacity={calculateOpacity} 
                />
                {(content.originalUrl?.includes('youtube.com') ||
                  content.originalUrl?.includes('youtu.be')) &&
                  viewMode === 'list' && (
                    <div className="w-full sm:w-48 aspect-video rounded overflow-hidden shadow-sm">
                      <img
                        src={`https://img.youtube.com/vi/${
                          content.originalUrl?.includes('youtu.be') 
                            ? content.originalUrl.split('/').pop().split('?')[0] 
                            : (content.originalUrl.match(/[?&]v=([^&]+)/)?.[1] || '') 
                        }/mqdefault.jpg`}
                        alt={content.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                  )}
                {!content.originalUrl?.includes('youtube.com') &&
                  !content.originalUrl?.includes('youtu.be') &&
                  viewMode === 'list' && (
                    <div className="w-full sm:w-48 aspect-video rounded overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-sm">
                      <div className="flex flex-col items-center space-y-1">
                        <span className="text-white font-medium">
                          {content.writingStyle || 'Content'}
                        </span>
                        <span className="text-white/70 text-sm">Custom Content</span>
                      </div>
                    </div>
                  )}
                {viewMode === 'grid' && (
                  <div className="mb-4">
                    {(content.originalUrl?.includes('youtube.com') || content.originalUrl?.includes('youtu.be')) ? (
                      <div className="w-full aspect-video rounded overflow-hidden shadow-sm mb-3">
                        <img
                          src={`https://img.youtube.com/vi/${
                            content.originalUrl?.includes('youtu.be') 
                              ? content.originalUrl.split('/').pop().split('?')[0] 
                              : (content.originalUrl.match(/[?&]v=([^&]+)/)?.[1] || '') 
                          }/mqdefault.jpg`}
                          alt={content.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                      </div>
                    ) : (
                      <div className="w-full aspect-video rounded overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-sm mb-3">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-white font-medium">
                            {content.writingStyle || 'Content'}
                          </span>
                          <span className="text-white/70 text-sm">Custom Content</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <h3 
                      className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate pr-2 title-text cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 group flex items-center"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        // Open edit modal when clicking on title
                        setEditingContentId(content.id);
                        setEditTitle(content.title || 'Untitled');
                        setEditWritingStyle(content.writingStyle || '');
                        setEditSummary(content.summary || '');
                        setEditOriginalUrl(content.originalUrl || '');
                        setIsEditModalOpen(true);
                      }}
                    >
                      <span className="truncate">{content.title || 'Untitled'}</span>
                      <PencilIcon className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newStarredStatus = !content.isStarred;
                          updateDoc(doc(db, 'contents', content.id), {
                            isStarred: newStarredStatus
                          });
                          setContents((prevContents) =>
                            prevContents.map((c) =>
                              c.id === content.id ? { ...c, isStarred: newStarredStatus } : c
                            )
                          );
                        }}
                        className={`star-button flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-colors ${
                          content.isStarred
                            ? 'text-yellow-500 hover:text-yellow-600'
                            : 'text-gray-400 hover:text-gray-500'
                        }`}
                        aria-label={
                          content.isStarred ? t('ui.unstar') : t('ui.star')
                        }
                        title={
                          content.isStarred ? t('ui.unstar') : t('ui.star')
                        }
                      >
                        {content.isStarred ? (
                          <StarIconSolid className="w-5 h-5" />
                        ) : (
                          <StarIcon className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVisibilityToggle(content.id, content.isPublic);
                        }}
                        className={`control-button flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full transition-colors ${
                          content.isPublic
                            ? 'text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300 hover:bg-green-400/10'
                            : 'text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-400/10'
                        }`}
                        aria-label={
                          content.isPublic
                            ? t('ui.makePrivate')
                            : t('ui.makePublic')
                        }
                        title={
                          content.isPublic
                            ? t('ui.makePrivate')
                            : t('ui.makePublic')
                        }
                      >
                        {content.isPublic ? (
                          <EyeIcon className="w-5 h-5" />
                        ) : (
                          <EyeSlashIcon className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(content.id);
                        }}
                        className="control-button flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-400/10 transition-colors"
                        aria-label={t('ui.delete')}
                        title={t('ui.delete')}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {content.writingStyle && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-400/10 text-purple-800 dark:text-purple-400">
                          {content.writingStyle}
                        </span>
                      )}
                      {content.language && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-400/10 text-teal-800 dark:text-teal-400">
                          {content.language === 'en' ? 'English' :
                           content.language === 'zh' ? '中文' :
                           content.language === 'es' ? 'Español' :
                           content.language === 'fr' ? 'Français' :
                           content.language === 'de' ? 'Deutsch' :
                           content.language === 'it' ? 'Italiano' :
                           content.language === 'pt' ? 'Português' :
                           content.language === 'ru' ? 'Русский' :
                           content.language === 'ja' ? '日本語' :
                           content.language === 'ko' ? '한국어' :
                           content.language || 'unknown'}
                        </span>
                      )}
                      <span className="text-gray-500 dark:text-gray-400 text-xs">
                        {new Date(content.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      {content.externalUrl && (
                        <div className="w-full" onClick={(e) => e.stopPropagation()}>
                          {content.isExpanded ? (
                            <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col">
                              <div className="bg-gray-100 dark:bg-gray-800 p-4 flex items-center gap-4 shadow-lg">
                                <button
                                  onClick={() => {
                                    setContents((prevContents) =>
                                      prevContents.map((c) =>
                                        c.id === content.id ? { ...c, isExpanded: false } : c
                                      )
                                    );
                                  }}
                                  className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                    />
                                  </svg>
                                  {t('ui.backToHistory')}
                                </button>
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                                  {content.title}
                                </h2>
                              </div>
                              <div className="flex-1 overflow-hidden">
                                <iframe
                                  src={content.externalUrl}
                                  className="w-full h-full"
                                  frameBorder="0"
                                  title={content.title}
                                />
                              </div>
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      )}
                      {!content.externalUrl && (
                        <Link
                          to={`/viewer/${content.id}`}
                          className="inline-flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 transition-colors"
                        >
                          <span>{t('ui.viewContent')}</span>
                          <ArrowUpTrayIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('ui.editContent')}</h2>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ui.title')}</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('ui.titlePlaceholder')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ui.writingStyle')}</label>
                  <input
                    type="text"
                    value={editWritingStyle}
                    onChange={(e) => setEditWritingStyle(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('ui.writingStylePlaceholder')}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ui.summary')}</label>
                  <textarea
                    value={editSummary}
                    onChange={(e) => setEditSummary(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-24"
                    placeholder={t('ui.summaryPlaceholder')}
                  ></textarea>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ui.originalUrl')}</label>
                  <input
                    type="text"
                    value={editOriginalUrl}
                    onChange={(e) => setEditOriginalUrl(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder={t('ui.originalUrlPlaceholder')}
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('ui.cancel')}
                  </button>
                  <button
                    onClick={handleUpdateContent}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    {t('ui.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}